/**
 * The app navigator (formerly "AppNavigator" and "MainNavigator") is used for the primary
 * navigation flows of your app.
 * Generally speaking, it will contain an auth flow (registration, login, forgot password)
 * and a "main" flow which the user will use once logged in.
 */
import React, { FC, useCallback, useEffect, useMemo } from "react"
import { Appearance, Platform, Pressable, StyleSheet } from "react-native"
import {
  DarkTheme,
  DefaultTheme,
  Link,
  LinkingOptions,
  NavigationContainer,
} from "@react-navigation/native"
// import { createNativeStackNavigator } from "@react-navigation/native-stack"
import { createStackNavigator } from "@react-navigation/stack"
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs"
import {
  DemoListScreen,
  DemoScreen, GraphScreen, HelpScreen, HomeNotification,
  HomeRefresh,
  HomeScreen,
  HomeTitle,
  InitialSyncScreen, ProfileScreen,
  QuickInputScreen,
  WelcomeScreen,
} from "../screens"
import { navigationRef, useBackButtonHandler, bottomTabPressListener } from "./navigation-utilities"
import { LoginNavigator } from "./login/login-navigator"
import { HomeNavigator } from "./home/home-navigator"
import { GraphNavigator } from "./graph/graph-navigator"
import { QuickInputNavigator } from "./quick-input/quick-input-navigator"
import { HelpNavigator } from "./help/help-navigator"
import { ProfileNavigator } from "./profile/profile-navigator"
import { color } from "../theme"
import { JalaIcon, Text, WalkthroughElement } from "../components"
import { View } from "native-base"
import { translate } from "../i18n"
import { PostHogAutocaptureOptions, PostHogOptions, PostHogProvider } from "posthog-react-native"
import JalaAnalytic from "../utils/jala-analytic"
import { BottomTabBarButtonProps } from "@react-navigation/bottom-tabs/src/types"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { observer } from "mobx-react-lite"
import { useStores } from "../models"
import { AutoFeederPondNavigator as HomeAutoFeederPondNavigator } from "./home/auto-feeder-pond/auto-feeder-pond-navigator"

export * from "./home/home-navigator";
export * from "./graph/graph-navigator";
export * from "./quick-input/quick-input-navigator";
export * from "./help/help-navigator";
export * from "./profile/profile-navigator";

/**
 * This type allows TypeScript to know what routes are defined in this navigator
 * as well as what properties (if any) they might take when navigating to them.
 *
 * If no params are allowed, pass through `undefined`. Generally speaking, we
 * recommend using your MobX-State-Tree store(s) to keep application state
 * rather than passing state through navigation params.
 *
 * For more information, see this documentation:
 *   https://reactnavigation.org/docs/params/
 *   https://reactnavigation.org/docs/typescript#type-checking-the-navigator
 */
export type NavigatorParamList = {
  'Login.Stack': undefined,
  'App.Stack': undefined,

  'Home.Stack': undefined,
  'Home.AutoFeederPond.Stack': undefined,
  'Graph.Stack': undefined,
  'QuickInput.Stack': undefined,
  'Help.Stack': undefined,
  'Profile.Stack': undefined,

  'App.InitialSync': undefined,
  'App.Welcome': undefined,
  'App.Demo': undefined,
  'App.DemoList': undefined
}

export type AppNavigatorParamList = {
  'Home.Home': undefined,
  'Graph.Graph': undefined,
  'QuickInput.QuickInput': undefined,
  'Help.Help': undefined,
  'Profile.Profile': undefined,

  'App.InitialSync': undefined,
  'App.Welcome': undefined,
  'App.Demo': undefined,
  'App.DemoList': undefined
  // 🔥 Your screens go here
}

// Documentation: https://reactnavigation.org/docs/stack-navigator/
// const Stack = createNativeStackNavigator<NavigatorParamList>()
const Stack = createStackNavigator<NavigatorParamList>()
const BottomTab = createBottomTabNavigator<AppNavigatorParamList>()

export const AppStack = {
  index: 0,
  routes: [{ name: 'App.Stack' }]
};

export const AppStackInitialSync = {
  index: 0,
  routes: [{ name: 'App.InitialSync' }]
}

const linking: LinkingOptions<any> = {
  prefixes: ['jala://'],
}

interface NavigationProps extends Partial<React.ComponentProps<typeof NavigationContainer>> {}

const tabBarButtonStyle = StyleSheet.create({
  button: { display: 'flex' }
})

const TabBarButton: FC = ({
  children,
  style,
  onPress,
  to,
  accessibilityRole,
  ...rest
}: BottomTabBarButtonProps) => {
  if (Platform.OS === 'web' && to) {
    // React Native Web doesn't forward `onClick` if we use `TouchableWithoutFeedback`.
    // We need to use `onClick` to be able to prevent default browser handling of links.
    return (
      <Link
        {...rest}
        to={to}
        style={[tabBarButtonStyle.button, style]}
        onPress={(e: any) => {
          if (
            !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) && // ignore clicks with modifier keys
            (e.button == null || e.button === 0) // ignore everything but left clicks
          ) {
            e.preventDefault();
            onPress?.(e);
          }
        }}
      >
        {children}
      </Link>
    );
  } else {
    return (
      <Pressable
        {...rest}
        accessibilityRole={accessibilityRole}
        onPress={onPress}
        style={style}
      >
        {children}
      </Pressable>
    );
  }
}

const TabBarIconChat: FC<{
  colorScheme?: "light"|"dark",
  focused: boolean,
  color: string,
  size: number
}> = observer(function(props) {
  const { color, size } = props;
  const { homeStore, homeStore: { unreadConversationCount } } = useStores()

  useEffect(() => {
    homeStore.getUnreadConversationCount();
  }, [homeStore])

  return <View>
    {!!unreadConversationCount && <View rounded="full" size="8px" backgroundColor="red.500" alignSelf="flex-end" mb="-8px" zIndex={2} />}
    <JalaIcon name="chat" color={color} size={size} />
  </View>
})

const AppNavigator_ = () => {
  const insets = useSafeAreaInsets()
  const tabPress = useCallback((e) => {
    const index = [
      'Home.Home',
      'Graph.Graph',
      'QuickInput.QuickInput',
      'Help.Help',
      'Profile.Profile'
    ].findIndex((d) => e.target.includes(d))
    if (index === 2) e.preventDefault()
    return bottomTabPressListener(index)
  }, [])

  return (
    <BottomTab.Navigator
      screenOptions={{
        headerShown: false,
        headerTitleAlign: "left",
        tabBarLabelPosition: "below-icon",
        tabBarStyle: { borderTopColor: color.palette.offWhite },
      }}
      screenListeners={{
        tabPress
      }}
      initialRouteName="Home.Home"
    >
      <BottomTab.Screen
        name="Home.Home"
        component={HomeScreen}
        options={({ navigation, route }) => ({
          title: 'Home',
          headerTitle(props) {
            return Platform.OS !== 'ios' && <HomeTitle/>;
          },
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary, borderBottomWidth: 0 },
          headerTintColor: color.palette.white,
          headerLeft(props) {
            return Platform.OS === 'ios' && <View paddingLeft="8px">
              <HomeTitle/>
            </View>
          },
          headerRight(props) {
            return <View flexDirection="row">
              <HomeRefresh navigation={navigation} route={route} />
              <HomeNotification/>
            </View>
          },
          tabBarLabel({ focused, color }) {
            return <Text preset={focused ? "bottomLabel1" : "bottomLabel2" } color={color} tx="bottomMenu.home" />
          },
          tabBarActiveTintColor: color.primary,
          tabBarInactiveTintColor: color.palette.dust500,
          tabBarActiveBackgroundColor: color.palette.white,
          tabBarInactiveBackgroundColor: color.palette.white,
          tabBarIcon({ color, size }) {
            return <JalaIcon name="home" color={color} size={size} />
          },
          tabBarButton(props) {
            return (<View flex={1}>
              <TabBarButton {...props}>
                {props.children}
              </TabBarButton>
            </View>)
          },
        })}
      />
      <BottomTab.Screen
        name="Graph.Graph"
        component={GraphScreen}
        options={{
          title: 'Graph',
          headerTitle: translate('graphScreen.header'),
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary, borderBottomWidth: 0 },
          headerTintColor: color.palette.white,
          tabBarLabel({ focused, color }) {
            return <Text preset={focused ? "bottomLabel1" : "bottomLabel2" } color={color} tx="bottomMenu.graph" />
          },
          tabBarActiveTintColor: color.primary,
          tabBarInactiveTintColor: color.palette.dust500,
          tabBarActiveBackgroundColor: color.palette.white,
          tabBarInactiveBackgroundColor: color.palette.white,
          tabBarIcon({ color, size }) {
            return <JalaIcon name="chart" color={color} size={size} />
          },
          tabBarButton(props) {
            return (<View flex={1}>
              <TabBarButton {...props}>
                {props.children}
              </TabBarButton>
            </View>)
          },
        }}
      />
      <BottomTab.Screen
        name="QuickInput.QuickInput"
        component={QuickInputScreen}
        options={{
          // tabBarLabel: translate("bottomMenu.quickInput"),
          tabBarLabel({ focused, color }) {
            return <Text preset={focused ? "bottomLabel1" : "bottomLabel2" } color={color} tx="bottomMenu.quickInput" />
          },
          tabBarActiveTintColor: color.primary,
          tabBarInactiveTintColor: color.palette.dust500,
          tabBarActiveBackgroundColor: color.palette.white,
          tabBarInactiveBackgroundColor: color.palette.white,
          tabBarIcon({ size }) {
            return <View
              h="54px" w="54px"
              borderRadius="27px"
              alignItems="center"
              justifyContent="center"
              backgroundColor={color.primary}
              top="-14px" bottom="14px"
              shadow="4"
            >
              <JalaIcon name="add" color={color.palette.white} size={size} />
            </View>
          },
          tabBarButton(props) {
            return (<View flex={1}>
              <WalkthroughElement id="tab-bar-quick-input" tooltipProps={{
                parentWrapperStyle: { height: 48 },
                tooltipStyle: { top: null, bottom: 72 + insets.bottom, left: 0, right: 0 }
              }}>
                <TabBarButton {...props}>
                  {props.children}
                </TabBarButton>
              </WalkthroughElement>
            </View>)
          },
        }}
      />
      <BottomTab.Screen
        name="Help.Help"
        component={HelpScreen}
        options={{
          // tabBarLabel: translate("bottomMenu.help"),
          tabBarLabel({ focused, color }) {
            return <Text preset={focused ? "bottomLabel1" : "bottomLabel2" } color={color} tx="bottomMenu.help" />
          },
          tabBarActiveTintColor: color.primary,
          tabBarInactiveTintColor: color.palette.dust500,
          tabBarActiveBackgroundColor: color.palette.white,
          tabBarInactiveBackgroundColor: color.palette.white,
          tabBarIcon(props) {
            return (<TabBarIconChat {...props} />)
          },
          tabBarButton(props) {
            return (<View flex={1}>
              <TabBarButton {...props}>
                {props.children}
              </TabBarButton>
            </View>)
          },
        }}
      />
      <BottomTab.Screen
        name="Profile.Profile"
        component={ProfileScreen}
        options={{
          title: 'Profile',
          headerTitle: translate('profileScreen.profile'),
          headerShown: true,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: color.primary, borderBottomWidth: 0 },
          headerTintColor: color.palette.white,
          // tabBarLabel: translate("bottomMenu.profile"),
          tabBarLabel({ focused, color }) {
            return <Text preset={focused ? "bottomLabel1" : "bottomLabel2" } color={color} tx="bottomMenu.profile" />
          },
          tabBarActiveTintColor: color.primary,
          tabBarInactiveTintColor: color.palette.dust500,
          tabBarActiveBackgroundColor: color.palette.white,
          tabBarInactiveBackgroundColor: color.palette.white,
          tabBarIcon({ color, size }) {
            return <JalaIcon name="person" color={color} size={size} />
          },
          tabBarButton(props) {
            return (<View flex={1}>
              <TabBarButton {...props}>
                {props.children}
              </TabBarButton>
            </View>)
          }
        }}
      />
    </BottomTab.Navigator>
  )
}

export const AppNavigator = (props: NavigationProps) => {
  const colorScheme = "light" || Appearance.getColorScheme()
  useBackButtonHandler(canExit)

  const autoCapture = useMemo<boolean|PostHogAutocaptureOptions>(() => ({
    captureTouches: false
  }), [])

  const postHogOptions = useMemo<PostHogOptions>(() => ({
    persistence: Platform.OS === 'web' ? "memory" : "file"
  }), [])

  return (
    <NavigationContainer
      ref={navigationRef}
      theme={colorScheme === "dark" ? DarkTheme : DefaultTheme}
      linking={linking}
      {...props}
    >
      <PostHogProvider
        client={JalaAnalytic.postHog}
        autocapture={autoCapture}
        options={postHogOptions}
      >
        <Stack.Navigator
          screenOptions={{
            cardStyle: { flex: 1 },
            headerShown: false,
            headerBackTitleVisible: false,
            headerTitleAlign: "left",
            headerTitleContainerStyle: { marginLeft: 0 }
          }}
          initialRouteName="Login.Stack"
        >
          <Stack.Screen name="Login.Stack">{LoginNavigator}</Stack.Screen>
          <Stack.Screen name="App.Stack">{AppNavigator_}</Stack.Screen>

          <Stack.Screen name="Home.Stack">{HomeNavigator}</Stack.Screen>
          <Stack.Screen name="Home.AutoFeederPond.Stack">{HomeAutoFeederPondNavigator}</Stack.Screen>
          <Stack.Screen name="Graph.Stack">{GraphNavigator}</Stack.Screen>
          <Stack.Screen name="QuickInput.Stack">{QuickInputNavigator}</Stack.Screen>
          <Stack.Screen name="Help.Stack">{HelpNavigator}</Stack.Screen>
          <Stack.Screen name="Profile.Stack">{ProfileNavigator}</Stack.Screen>

          <Stack.Screen name="App.InitialSync" component={InitialSyncScreen} />
          <Stack.Screen name="App.Welcome" component={WelcomeScreen} />
          <Stack.Screen name="App.Demo" component={DemoScreen} />
          <Stack.Screen name="App.DemoList" component={DemoListScreen} />
        </Stack.Navigator>
      </PostHogProvider>
    </NavigationContainer>
  )
}

AppNavigator.displayName = "AppNavigator"

/**
 * A list of routes from which we're allowed to leave the app when
 * the user presses the back button on Android.
 *
 * Anything not on this list will be a standard `back` action in
 * react-navigation.
 *
 * `canExit` is used in ./app/app.tsx in the `useBackButtonHandler` hook.
 */
const exitRoutes = ["App.Welcome", "Quiz.Start"]
export const canExit = (routeName: string) => exitRoutes.includes(routeName)
